import * as Immutable from 'immutable';
import * as React from 'react';
import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { connect } from 'react-redux';
import { Filter } from 'react-table';
import { Dispatch } from 'redux';
import { Popup } from 'semantic-ui-react';

import { removeDetail, updateAlphaProperties } from '../../../actions/AlphasActions';
import { changeInnerCorrelationColors, deleteListTag, updateUserList } from '../../../actions/AlphaListsActions';
import analytics from '../../../shared/services/analytics/analytics';
import { isAction } from '../../../shared/helpers/fetch-helper';
import { ErrorBoundary } from '../../../containers/ErrorBoundary';
import { localeData } from '../../../locales/data';
import { StateType } from '../../../reducers';
import { AlphaColumn, AlphaColumnBase } from '../../../reducers/AlphasReducer/AlphasReducer.types';
import {
  alphaIsSuperSelector,
  alphaMyAlphasCountSelector,
  alphaMyAlphasMessageSelector,
  alphaMyAlphasResultsSelector,
  alphaMyAlphasSelector,
  alphaSelectedColumnsSelector,
  alphasFetchingDataSelector,
  alphaShowDetailSelector,
  alphasOptionsSelector,
  alphasOptionsSettingsChildrenSelector,
  alphaSuperAlphaPropertiesNameSelector,
  pathHasSuperSelectionSelector,
} from '../../../reducers/AlphasReducer/AlphasSelector';
import {
  alphaListCurrentListIdSelector,
  alphaListCurrentListNameSelector,
  alphaListCurrentListSelector,
  alphaListFetchingInnerCorrelationSelector,
  alphaListInnerAndSelfIsAvailableSelector,
  alphaListInnerAndSelfMessageSelector,
  alphaListInnerCorrelationDataSelector,
} from '../../../reducers/AlphaListsReducer/AlphaListsSelector';
import { alphaPropertiesErrorOsmosisPointsSelector } from '../../../reducers/AlphaPropertiesReducer/AlphaPropertiesSelector';
import { queryClient, OSMOSIS_SUMMARY_QUERY_KEY } from '../../../shared/api/apiHooks';
import { getUserIdSelector } from '../../../reducers/AuthenticationReducer/AuthenticationSelector';
import { expressionOperatorsSelector } from '../../../reducers/ExpressionLanguageReducer/ExpressionLanguageSelector';
import { fastExpressionOperatorsSelector } from '../../../reducers/FastExpressionLanguageReducer/FastExpressionLanguageSelector';
import {
  languageComboIdsSelector,
  languageFieldIdsSelector,
  languageSelectionIdsSelector,
} from '../../../reducers/LanguageFieldsReducer/LanguageFieldsSelector';
import { DatetimeFilters } from '../../../shared/data';
import { editorWillMountMonaco } from '../../../shared/helpers/monaco-helper';
import { AlphaTableType, DispatchConnect } from '../../../types';
import HOCIsMobile, { HOCIsMobileState } from '../../HOCIsMobile/HOCIsMobile';
import { SelectColumns } from '../../Table';
import {
  addToList,
  codeIcon,
  computeTableData,
  deleteAlphaIcon,
  favoriteIcon,
  GeneralFilter,
  SingleCheckboxCell,
  TableCell,
  TableHeader,
  visibilityIcon,
} from '../../Table/Table';
import InnerChartContainer from '../InnerChart/InnerChartContainer';
import InnerCorrelationContainer from '../InnerCorrelation/InnerCorrelationContainer';

import { AlphasList } from './AlphasList';
import { AlphaRenameList } from './AlphaRenameList';
import { AlphaTableFilter } from './AlphaTableFilter';
import './AlphaTable.scss';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import { getIsColumnAvailable, isStatusColumnAvailable } from '../shared/columns';

interface AlphaTableProps {
  handleShowDetail: (alphaIndex: string) => void;
  handleSetCurrentAlpha: (alphaIndex: string) => void;
  isOpenSelectColumns: boolean;
  optionsModal: Immutable.List<AlphaColumn>;
  handleCheckBox: (e: any, d: Object) => void;
  handleApply: (e: any) => void;
  handleReset: (e: any) => void;
  handleClosePopup: () => void;
  handleOpen: () => void;
  handleFavorite: (id: string) => void;
  dataAddToList: any;
  handlePageChange: (pageIndex: number) => void;
  handlePageSizeChange: (pageSize: number) => void;
  handleFilteredChange: (newFiltering: Filter[], column: any, value: any) => void;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  sort?: string;
  order: boolean;
  handleSortChange: (id: string) => void;
  handleSetSort: (id: string, isAscending: boolean) => void;
  handleSingleCheckbox: (data: any) => void;
  handleBulkCheckbox: () => void;
  bulkAlphas: string[];
  allChecked: boolean;
  filters: any;
  handleFilterBlur: () => void;
  filtersUpdated: boolean;
  pageSizeLimit: number;
  handleVisualization: (id: string) => void;
  type: AlphaTableType;
  handleDeleteAlphas: (alphas: any) => void;
  changePathToAlphas: () => void;
  alphasChoicesOptions: any;
}

interface AlphaTableStateProps {
  alphas: any;
  alphasResults: any;
  showDetail: boolean;
  expressionOperators: any;
  fastExpressionOperators: any;
  selectedColumns: Immutable.List<AlphaColumn>;
  alphasOptions: Immutable.Map<string, any>;
  settingsOptions: Immutable.Map<string, any>;
  alphaCount: number;
  alphaMessage: string;
  fetchingData: boolean;
  listName: string;
  listId: string;
  currentList: any;
  innerAndSelfIsAvailable: boolean;
  isFetchingInnerCorrelation: boolean;
  innerCorrelationData: Immutable.Map<string, any>;
  innerSelfMessage: string;
  superAlphaName: string;
  isSuperAlpha: boolean;
  isSuperSelection: boolean;
  userId: string;
  languageFieldsRegularIds: string[];
  languageFieldsSelectionIds: string[];
  languageFieldsComboIds: string[];
  alphaPropertiesErrorOsmosisPoints: any;
}

interface AlphaTableStateDispatchers {
  dispatchDeleteList: (tagId: string) => void;
  dispatchRenameList: (tagId: string, id: string) => void;
  dispatchRemoveDetail: () => void;
  dispatchChangeInnerCorrelationColors: (info: any) => void;
  dispatchUpdateAlphaProperties: (alphaId: string, data: any) => Promise<any>;
}

interface AlphaTableState {
  columns: any;
  openModal: boolean;
  listName: string;
  alphasInfo: any;
  tableMaxWidth: number | null;
  tableMinWidth: number | null;
}

type AlphaTableGeneric = AlphaTableProps & AlphaTableStateProps & AlphaTableStateDispatchers & InjectedIntlProps & HOCIsMobileState;

export class AlphaTable extends React.Component<AlphaTableGeneric, AlphaTableState> {
  constructor(props: AlphaTableGeneric) {
    super(props);
    this.state = {
      openModal: false,
      listName: '',
      columns: [],
      alphasInfo: null,
      tableMaxWidth: null,
      tableMinWidth: 0,
    };
  }

  componentWillUnmount() {
    this.props.dispatchRemoveDetail();
    window.removeEventListener('resize', this.updateDimensions);
  }

  componentDidMount() {
    if (this.props.isSuperSelection) {
      this.props.handleSetSort('weight', false);
    }
    if (this.props.alphas && this.props.alphas.get('results')) {
      this.setState({
        alphasInfo: this.getInfo(this.props.alphas.get('results').toJS()),
        listName: this.props.currentList !== null ? this.props.currentList.get('name') : '',
      });
    }
    if (this.props.selectedColumns) {
      this.setState(
        {
          columns: this.getColumns(this.props.selectedColumns),
        },
        () => this.setTableWidth(this.state.columns)
      );
    }
    window.addEventListener('resize', this.updateDimensions);

    this.updateDimensions();
  }

  componentDidUpdate(prevProps: AlphaTableGeneric) {
    if (
      (this.props.alphas &&
        this.props.alphas.get('results') &&
        prevProps.alphas &&
        (this.compareIfAlphasChanged(prevProps.alphas.get('results'), this.props.alphas.get('results')) ||
          prevProps.alphas.get('count') !== this.props.alphas.get('count'))) ||
      (this.props.alphas && this.props.alphas.get('results') && !prevProps.alphas)
    ) {
      this.setState(
        {
          columns: this.getColumns(this.props.selectedColumns),
          alphasInfo: this.getInfo(this.props.alphas.get('results').toJS()),
          listName: this.props.currentList !== null ? this.props.currentList.get('name') : '',
        },
        () => this.setTableWidth(this.state.columns)
      );
    }
    if (
      prevProps.filtersUpdated !== this.props.filtersUpdated ||
      prevProps.allChecked !== this.props.allChecked ||
      prevProps.bulkAlphas.length !== this.props.bulkAlphas.length ||
      prevProps.isOpenSelectColumns !== this.props.isOpenSelectColumns ||
      this.props.alphasOptions !== prevProps.alphasOptions ||
      this.props.fetchingData !== prevProps.fetchingData ||
      this.compareActiveColumnsChanged(prevProps.optionsModal.toJS(), this.props.optionsModal.toJS()) ||
      this.compareActiveColumnsChanged(prevProps.selectedColumns.toJS(), this.props.selectedColumns.toJS())
    ) {
      this.setState(
        {
          columns: this.getColumns(this.props.selectedColumns),
        },
        () => this.setTableWidth(this.state.columns)
      );
    }
    if (!prevProps.innerCorrelationData && this.props.alphas && prevProps.innerCorrelationData !== this.props.innerCorrelationData) {
      this.setState(
        {
          alphasInfo: this.getInfo(this.props.alphas.get('results').toJS()),
        },
        () => this.setTableWidth(this.state.columns)
      );
    }
    if (prevProps.isMobile !== this.props.isMobile) {
      this.updateDimensions();
    }
  }

  compareActiveColumnsChanged = (prevList: any, currList: any): boolean => {
    if (prevList.length !== currList.length) {
      return true;
    }

    for (let i = 0; i < currList.length; i += 1) {
      if (prevList[i].active !== currList[i].active) {
        return true;
      }
    }

    return false;
  };

  updateDimensions = (): void => {
    const alphaMenuElement: HTMLElement = document.getElementById('alpha-menu')!;
    if (alphaMenuElement) {
      const paddingValue = !this.props.isMobile ? 16 * 2 : 0;
      this.setState({ tableMinWidth: alphaMenuElement.offsetWidth - paddingValue });
    }
  };

  compareIfAlphasChanged = (currentAlphas: any, newAlphas: any) => {
    if (currentAlphas.size !== newAlphas.size) {
      return true;
    }
    const arrayOfIds = currentAlphas.map((alpha: any) => alpha.get('id'));

    for (let i = 0; i < currentAlphas.size; i = i + 1) {
      if (arrayOfIds.indexOf(newAlphas.getIn([i, 'id'])) === -1) {
        return true;
      }
    }

    return false;
  };

  editorWillMount = () => () => {
    const { expressionOperators, fastExpressionOperators, languageFieldsRegularIds, languageFieldsSelectionIds, languageFieldsComboIds } =
      this.props;

    editorWillMountMonaco({
      expressionOperators,
      fastExpressionOperators,
      languageFieldsRegularIds,
      languageFieldsSelectionIds,
      languageFieldsComboIds,
    })();
  };

  getModifiers = () => (this.props.showDetail ? ['active'] : []);

  getTableData = (options: any) => {
    if (options) {
      return computeTableData(this.props.alphasResults, this.editorWillMount, this.props.handleShowDetail, options.toJS());
    }
  };

  setTableWidth = (columns: any) => {
    const alphaTableContainerElement: HTMLElement = document.getElementById('alpha-table-container')!;
    if (alphaTableContainerElement) {
      let totalWidth = 0;
      columns.map((element: any) => {
        element.columns.map((column: any) => {
          if (column.width) {
            totalWidth += column.width;
          } else if (column.maxWidth) {
            totalWidth += column.maxWidth;
          }
        });
      });
      const paddingValue = window.getComputedStyle(alphaTableContainerElement).getPropertyValue('padding-left');
      // Add left & right padding and 1px to avoid overflowing
      totalWidth += parseInt(paddingValue, 10) * 2 + 1;

      if (this.state.tableMaxWidth !== totalWidth) {
        this.setState({ tableMaxWidth: totalWidth });
      }
    }
  };

  setTableColumns = (options: Immutable.List<AlphaColumn>) => {
    const isDisabled = this.getDisabled();
    const isHidden: AlphaColumn = options.filter((column: AlphaColumn) => column.get('id') === 'hidden').first();
    let columns = [
      codeIcon({ settingsOptions: this.props.settingsOptions, userId: this.props.userId, isSuper: isDisabled }),
      this.props.type === 'alphaTag'
        ? deleteAlphaIcon((val: string) => this.props.handleDeleteAlphas([val]), this.props.intl)
        : addToList({
            data: this.props.dataAddToList,
            handleSetCurrentAlpha: this.props.handleSetCurrentAlpha,
            intl: this.props.intl,
            isSuper: isDisabled,
            // BRAINTECH-5372: per-row Add-to-list event for the Alphas page.
            // Threaded through the generic Table.addToList helper so Table.tsx
            // remains GA-agnostic.
            onTrackOpen: () => analytics.trackCustomEvent('alphas_click_add_to_list', { event_label: 'row' }),
          }),
    ];

    if (isHidden.get('active') === true) {
      columns = columns.concat(visibilityIcon(this.props.handleVisualization, this.props.intl));
    }

    return columns;
  };

  private getDisabled() {
    return this.props.type === 'superAlpha' || this.props.type === 'superSelection';
  }

  handleSaveOsmosisPoints = async (alphaId: string, value: number | null): Promise<any> => {
    const result = await this.props.dispatchUpdateAlphaProperties(alphaId, { osmosisPoints: value });

    if (isAction(result, 'RESPONSE_UPDATE_ALPHA_PROPERTIES_FAILURE')) {
      const osmosisPointsErrors = this.props.alphaPropertiesErrorOsmosisPoints;

      if (osmosisPointsErrors && osmosisPointsErrors.size && osmosisPointsErrors.size > 0) {
        throw new Error(osmosisPointsErrors.get(0));
      }

      throw new Error('Failed to update Osmosis Points.');
    }

    // Saving an alpha's osmosis points can flip a scope's server-computed `scalable` flag in the
    // summary; invalidate so the Osmosis Scale button reflects updated eligibility without waiting
    // out the summary query's 1-hour staleTime.
    queryClient.invalidateQueries({ queryKey: OSMOSIS_SUMMARY_QUERY_KEY });

    return result;
  };

  getOptionsModal = () => {
    return this.props.optionsModal.filter(
      column =>
        getIsColumnAvailable({ column, alphaOptions: this.props.alphasOptions }) &&
        isStatusColumnAvailable({ column, type: this.props.type })
    );
  };

  getColumns = (options: any) => {
    const isLoadingTable = this.props.fetchingData;
    const filteredOptions = options
      .filter(
        (column: AlphaColumn) =>
          getIsColumnAvailable({ column, alphaOptions: this.props.alphasOptions }) &&
          isStatusColumnAvailable({ column, type: this.props.type }) &&
          column.get('active') === true &&
          column.get('display') === true &&
          !column.get('iconColumn')
      )
      .toJS();
    const sortType = this.props.sort ? this.props.sort : 'dateCreated';
    const columns: [any] = filteredOptions.map((column: AlphaColumnBase) => ({
      Header: TableHeader({ name: column.name, sort: sortType, order: this.props.order, onClick: this.props.handleSortChange }),
      columns: [
        {
          accessor: column.id,
          width: column?.width ?? (DatetimeFilters.includes(column.id) ? 150 : 120),
          Cell: TableCell(this.props.alphasChoicesOptions, this.handleSaveOsmosisPoints),
          Filter: AlphaTableFilter({
            alphasOptions: this.props.alphasOptions,
            column,
            filters: this.props.filters ?? {},
            filtersUpdated: this.props.filtersUpdated,
            handleFilterBlur: this.props.handleFilterBlur,
            tabType: this.props.type,
          }),
        },
      ],
    }));

    const firstColumns = this.setTableColumns(options);

    const trackedHandleFavorite = (id: string) => {
      analytics.trackCustomEvent('alphas_click_favorite', { event_label: 'row' });
      this.props.handleFavorite(id);
    };

    const trackedHandleCheckBox = (e: any, data: any) => {
      analytics.trackCustomEvent('alphas_toggle_column', { event_label: data?.value ?? 'unknown' });
      this.props.handleCheckBox(e, data);
    };

    const trackedHandleReset = (e: any) => {
      analytics.trackCustomEvent('alphas_click_select_columns_reset');
      this.props.handleReset(e);
    };

    const isFavorite: AlphaColumn = options.filter((column: AlphaColumn) => column.get('id') === 'favorite').first();
    const isFavoriteActive = isFavorite?.get('active') === true;

    const lastColumns = [
      ...(isFavoriteActive ? [favoriteIcon(trackedHandleFavorite, this.props.intl, this.getDisabled())] : []),
      {
        Cell: SingleCheckboxCell({
          handleSingleCheckbox: this.props.handleSingleCheckbox,
          intl: this.props.intl,
          bulkAlphas: this.props.bulkAlphas,
          isSuper: this.getDisabled(),
        }),
        maxWidth: 35,
        Filter: GeneralFilter(this.props.handleBulkCheckbox, this.props.intl, this.props.allChecked, isLoadingTable),
      },
    ];

    // The header-group parent <th> width in react-table v6 is the sum of its leaf columns' widths.
    // The "Select Columns" header (icon + label) needs ~145px to render on one line. When toggling off
    // optional icon columns (favorite, visibility), the natural sum can fall below that, so scale every
    // leaf column up proportionally to preserve their relative ratios and distribute extra space evenly.
    const SELECT_COLUMNS_GROUP_MIN_WIDTH = 145;
    const subColumns = [...firstColumns, ...lastColumns];
    const naturalWidths = subColumns.map((col: any) => col.width ?? col.maxWidth ?? 35);
    const totalNaturalWidth = naturalWidths.reduce((sum: number, w: number) => sum + w, 0);
    let scaledSubColumns = subColumns;
    if (totalNaturalWidth < SELECT_COLUMNS_GROUP_MIN_WIDTH) {
      const floored = naturalWidths.map((w: number) => Math.floor((w / totalNaturalWidth) * SELECT_COLUMNS_GROUP_MIN_WIDTH));
      const remainder = SELECT_COLUMNS_GROUP_MIN_WIDTH - floored.reduce((sum: number, w: number) => sum + w, 0);
      const lastIndex = subColumns.length - 1;
      scaledSubColumns = subColumns.map((col: any, i: number) => ({
        ...col,
        // Add the rounding remainder to the last column so the group hits exactly the target width.
        width: floored[i] + (i === lastIndex ? remainder : 0),
        maxWidth: undefined,
      }));
    }

    const categories: [string] = options
      .map((column: AlphaColumn) => column.get('category'))
      .toSet() // Converting to set removes duplicates
      .toJS();
    columns.unshift({
      Header: (
        <ErrorBoundary showUI={true} isNotFullScreen={true}>
          <Popup
            trigger={
              <div className={`table__select-columns ${this.props.showDetail ? 'table__select-columns--disabled' : ''}`}>
                <FormattedMessageHelper
                  tagName="span"
                  id="alphasList.selectColumns"
                  defaultMessage={localeData.en['alphasList.selectColumns']}
                />
              </div>
            }
            open={this.props.isOpenSelectColumns && !this.props.showDetail}
            content={
              <SelectColumns
                categories={categories}
                // @ts-ignore
                options={this.getOptionsModal().toJS()}
                handleCheckBox={trackedHandleCheckBox}
                handleApply={this.props.handleApply}
                handleReset={trackedHandleReset}
                handleClosePopup={this.props.handleClosePopup}
              />
            }
            on="click"
            onOpen={() => {
              analytics.trackCustomEvent('alphas_click_select_columns');
              this.props.handleOpen();
            }}
            hideOnScroll={true}
            onClose={this.props.handleClosePopup}
            // There are some notes here:
            //   1. Because popperjs v2 only find an opposite placement as fallback. In the initial load,
            //      the bottom space is not enough for the popup. So that we need to add "right" as a fallback too.
            //   2. Setting position to "bottom left" and fallback to "right" don't work. Idk why (maybe I'll dive in later),
            //      but when the popup shows up in the right, its position is incorrect. And when we click to any checkbox, it
            //      recalculate the position which is correct now, however it causes a layout shift.
            //   3. Setting position to "right center" and fallback to "bottom-start" do work. (current solution)
            position="right center"
            popperModifiers={[{ name: 'flip', enabled: true, options: { fallbackPlacements: ['bottom-start', 'top-start'] } }]}
          />
        </ErrorBoundary>
      ),
      columns: scaledSubColumns,
    });

    return columns;
  };

  randDarkColor() {
    return `rgb(${Math.floor(Math.random() * 256)},${Math.floor(Math.random() * 256)}, ${Math.floor(Math.random() * 256)})`;
  }

  removeDuplicates(arr: any) {
    const newArray: any = [];
    const idOfArray: any = [];
    arr.map((val: any) => {
      if (idOfArray.indexOf(val.id) === -1) {
        newArray.push(val);
        idOfArray.push(val.id);
      }
    });

    return newArray;
  }

  getInfo = (alphasList: any) => {
    if (this.props.type === 'alphaTag') {
      const info = alphasList.map((alpha: any) => ({
        id: alpha.id,
        text: alpha.name === null ? 'anonymous' : alpha.name,
        color: this.randDarkColor(),
      }));

      this.props.dispatchChangeInnerCorrelationColors(info);

      return info;
    }
  };

  handleOpenModal = () => {
    this.setState({ openModal: true });
  };

  handleCloseModal = () => {
    this.setState({ openModal: false });
  };

  handleDeleteList = () => {
    this.props.dispatchDeleteList(this.props.listId);
    this.props.changePathToAlphas();
  };

  render() {
    const superAlphaName = this.props.isSuperSelection ? null : this.props.superAlphaName ? this.props.superAlphaName : 'Anonymous';

    return (
      <div
        id="alpha-table-container"
        className="alpha-table-container"
        style={{
          maxWidth: this.state.tableMaxWidth ? `${this.state.tableMaxWidth}px` : '100%',
          minWidth: `${this.state.tableMinWidth}px`,
        }}
      >
        <AlphaRenameList
          openModal={this.state.openModal}
          dispatchRenameList={this.props.dispatchRenameList}
          handleCloseModal={this.handleCloseModal}
          currentListName={this.props.listName ? this.props.listName : 'Anonymous'}
          listId={this.props.listId}
        />
        <AlphasList
          data={this.props.alphasResults}
          handlePageChange={this.props.handlePageChange}
          handlePageSizeChange={this.props.handlePageSizeChange}
          currentPage={this.props.currentPage}
          pageSize={this.props.pageSize}
          totalPages={this.props.totalPages}
          handleFilteredChange={this.props.handleFilteredChange}
          filters={this.props.filters}
          alphaCount={this.props.alphaCount}
          alphaMessage={this.props.alphaMessage}
          pageSizeLimit={this.props.pageSizeLimit}
          modifiers={this.getModifiers()}
          tableData={this.getTableData(this.props.selectedColumns)}
          columns={this.state.columns}
          type={this.props.type}
          tableName={this.props.listName ? this.props.listName : 'Anonymous'}
          superAlphaName={superAlphaName}
          isSuperAlpha={this.props.isSuperAlpha}
          isSuperSelection={this.props.isSuperSelection}
          handleOpenModal={this.handleOpenModal}
          handleDeleteList={this.handleDeleteList}
          isFetchingInnerCorrelation={this.props.isFetchingInnerCorrelation}
        />
        {this.props.type === 'alphaTag' && this.props.innerAndSelfIsAvailable ? (
          <div className="alpha-table__lists">
            <InnerChartContainer alphasInfo={this.state.alphasInfo} />
            <InnerCorrelationContainer alphasInfo={this.state.alphasInfo} />
          </div>
        ) : (
          <div className="alpha-table__lists--not-available">{this.props.innerSelfMessage}</div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: StateType): AlphaTableStateProps => ({
  alphas: alphaMyAlphasSelector(state),
  alphasResults: alphaMyAlphasResultsSelector(state),
  isSuperSelection: pathHasSuperSelectionSelector(state),
  isSuperAlpha: alphaIsSuperSelector(state),
  // @ts-ignore
  superAlphaName: alphaSuperAlphaPropertiesNameSelector(state),
  // @ts-ignore
  showDetail: alphaShowDetailSelector(state),
  expressionOperators: expressionOperatorsSelector(state),
  fastExpressionOperators: fastExpressionOperatorsSelector(state),
  // @ts-ignore
  selectedColumns: alphaSelectedColumnsSelector(state),
  // @ts-ignore
  alphasOptions: alphasOptionsSelector(state),
  // @ts-ignore
  alphaCount: alphaMyAlphasCountSelector(state),
  // @ts-ignore
  alphaMessage: alphaMyAlphasMessageSelector(state),
  // @ts-ignore
  fetchingData: alphasFetchingDataSelector(state),
  // @ts-ignore
  listName: alphaListCurrentListNameSelector(state),
  // @ts-ignore
  listId: alphaListCurrentListIdSelector(state),
  currentList: alphaListCurrentListSelector(state),
  // @ts-ignore
  innerAndSelfIsAvailable: alphaListInnerAndSelfIsAvailableSelector(state),
  // @ts-ignore
  isFetchingInnerCorrelation: alphaListFetchingInnerCorrelationSelector(state),
  // @ts-ignore
  innerCorrelationData: alphaListInnerCorrelationDataSelector(state),
  // @ts-ignore
  innerSelfMessage: alphaListInnerAndSelfMessageSelector(state),
  // @ts-ignore
  settingsOptions: alphasOptionsSettingsChildrenSelector(state),
  languageFieldsRegularIds: languageFieldIdsSelector(state),
  languageFieldsSelectionIds: languageSelectionIdsSelector(state),
  languageFieldsComboIds: languageComboIdsSelector(state),
  // @ts-ignore
  userId: getUserIdSelector(state),
  alphaPropertiesErrorOsmosisPoints: alphaPropertiesErrorOsmosisPointsSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<any>): AlphaTableStateDispatchers => ({
  dispatchDeleteList: async (tagId: string) => dispatch(deleteListTag(tagId)),
  dispatchRenameList: async (tagId: string, id: string) => dispatch(updateUserList(tagId, id, [], 'rename')),
  dispatchRemoveDetail: () => dispatch(removeDetail()),
  dispatchChangeInnerCorrelationColors: (info: any) => dispatch(changeInnerCorrelationColors(info)),
  dispatchUpdateAlphaProperties: async (alphaId: string, data: any) => dispatch(updateAlphaProperties(alphaId, data)),
});

export default connect<AlphaTableStateProps, DispatchConnect<AlphaTableStateDispatchers>, any, StateType>(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(HOCIsMobile(AlphaTable)));
